import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import leftImg from '../../../assets/images/icons/left.svg'
import rightImg from '../../../assets/images/icons/right.svg'
import { sampleImages, SamplesContext } from "../../../context/samplesContext"
import { SelectedImageInfo } from "../types"
import ReactLoading from "react-loading"
import { ImageWithLoading } from "../../../components/image/ImageWithLoading"

const CAROUSEL_VISIBLE_COUNT = 6
const IMAGE_GAP_PX = 10
const FALLBACK_CARD_WIDTH = 160

type ImageSelectProps = {
  selectedImage: SelectedImageInfo | null
  onSelectImage: (source: SelectedImageInfo['source'], image: SelectedImageInfo['image']) => void
}

export function ImageSelect({ selectedImage, onSelectImage }: ImageSelectProps) {
  const { rowSample } = useContext(SamplesContext)

  return (
    <div className="flex space-x-[7px] desktop-xmd:space-x-0 desktop-xmd:flex-col desktop-xmd:space-y-[10px]">
      <ImageContainer
        label={'採寸撮影'}
        images={rowSample.sampleImages || []}
        source="sample"
        selectedImage={selectedImage}
        onSelectImage={onSelectImage}
      />
      <ImageContainer
        label={'EC撮影'}
        images={rowSample.sampleEcImages || []}
        source="ec"
        selectedImage={selectedImage}
        onSelectImage={onSelectImage}
      />
    </div>
  )
}

interface ImageContainerProps {
  label: string
  images: sampleImages[]
}

function ImageContainer({ label, images, source, selectedImage, onSelectImage }: ImageContainerProps & {
  source: SelectedImageInfo['source']
  selectedImage: SelectedImageInfo | null
  onSelectImage: (source: SelectedImageInfo['source'], image: SelectedImageInfo['image']) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [offset, setOffset] = useState(0)
  const [scrollMetrics, setScrollMetrics] = useState({
    maxOffset: 0,
    step: FALLBACK_CARD_WIDTH + IMAGE_GAP_PX,
  })

  const refreshScrollMetrics = useCallback(() => {
    const container = containerRef.current
    const list = listRef.current
    if (!container || !list) return

    const maxOffset = Math.max(list.scrollWidth - container.clientWidth, 0)
    const firstCard = list.querySelector<HTMLDivElement>('[data-card="true"]')
    const cardWidth = firstCard ? firstCard.clientWidth : FALLBACK_CARD_WIDTH

    setScrollMetrics({
      maxOffset,
      step: cardWidth + IMAGE_GAP_PX,
    })
    setOffset(prev => Math.min(prev, maxOffset))
  }, [])

  useEffect(() => {
    refreshScrollMetrics()
    const handleResize = () => refreshScrollMetrics()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [refreshScrollMetrics])

  useEffect(() => {
    setOffset(0)
    requestAnimationFrame(() => {
      refreshScrollMetrics()
    })
  }, [images, refreshScrollMetrics])

  const handleNext = useCallback(() => {
    setOffset(prev => {
      if (prev >= scrollMetrics.maxOffset) return prev
      return Math.min(prev + scrollMetrics.step, scrollMetrics.maxOffset)
    })
  }, [scrollMetrics])

  const handlePrev = useCallback(() => {
    setOffset(prev => {
      if (prev <= 0) return 0
      return Math.max(prev - scrollMetrics.step, 0)
    })
  }, [scrollMetrics.step])

  const canScrollLeft = useMemo(() => offset > 0, [offset])
  const canScrollRight = useMemo(
    () => offset < scrollMetrics.maxOffset,
    [offset, scrollMetrics.maxOffset]
  )
  const sortedImages = useMemo(
    () => [...images].sort((a, b) => a.image_index - b.image_index),
    [images]
  )

  return (
    <div className="flex items-center justify-between h-[97px] max-w-[1000px] w-full min-w-[360px] mt-[10px] desktop-xmd:min-w-[600px] desktop-xmd:mt-[30px]">
      <div className="items-center text-white h-full w-[92px] hidden desktop-xmd:flex">
        <span className="w-2 h-full bg-white rounded-sm" />
        <span className="pl-[10px] text-[14px] font-semibold">{label}</span>
      </div>
      <div className="relative overflow-hidden w-full" ref={containerRef}>
        <div
          ref={listRef}
          className="flex space-x-[10px] transition-transform duration-300"
          style={{ transform: `translateX(-${offset}px)` }}
        >
          {sortedImages.length > 0 ? (
            sortedImages
              .map((image, index) => {
                const isSelected =
                  selectedImage?.source === source &&
                  selectedImage?.image.image_index === image.image_index
                return (
                  <button
                    type="button"
                    key={`${source}-${image.image_index}-${index}`}
                    data-card="true"
                    onClick={() => onSelectImage(source, image)}
                    className={`relative flex items-center justify-center min-w-[160px] h-[90px] rounded-[5px] bg-white p-[5px] overflow-hidden border-[3px] ${isSelected ? 'border-[#545899]' : 'border-transparent'}`}
                  >
                    <ImageWithLoading
                      src={image.thumbnail_url}
                      alt={`Slide ${index + 1}`}
                      className="max-w-full max-h-full object-contain transition-opacity duration-200"
                      loadingFallback={
                        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
                          <ReactLoading type="spin" color="#545899" width={24} height={24} />
                        </div>
                      }
                    />
                  </button>
                )
              })
          ) : (
            // 画像がない場合のPlaceholder
            Array.from({ length: CAROUSEL_VISIBLE_COUNT }).map((_, index) => (
              <div key={index} className="min-w-[160px] h-[90px] bg-white rounded-[5px]"></div>
            ))
          )}
        </div>
        <button
          onClick={handlePrev}
          disabled={!canScrollLeft}
          className="absolute flex justify-center items-center left-0 top-1/2 transform -translate-y-1/2 bg-[#000000B2] text-white w-[30px] h-[35px] disabled:opacity-40"
        >
          <img src={leftImg} alt='' />
        </button>
        <button
          onClick={handleNext}
          disabled={!canScrollRight}
          className="absolute flex justify-center items-center right-0 top-1/2 transform -translate-y-1/2 bg-[#000000B2] text-white w-[30px] h-[35px] disabled:opacity-40"
        >
          <img src={rightImg} alt='' />
        </button>
      </div>
    </div>
  )
}
